import { ActionIcon, Menu, Modal, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconDots, IconPencil, IconTrash } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BlurhashImage } from 'src/Components/BlurhashImage';
import UserAvatar from 'src/Components/NavBar/UserAvatar';
import { useDeleteCommunityFeedItem } from 'src/Hooks/Mutations/useDeleteCommunityFeedItem';
import { useCurrentAssetId } from 'src/Hooks/Shared/useCurrentAssetId';

import { MemoryModal } from '../MemoryModal';

import styles from './Memory.module.scss';
import { Props } from './Memory.props';

/**
 * One memory item that is shown in the list of memories at the top of the community screen.
 */
const Memory = ({ memory, shouldOpenModal, onModalOpened }: Props) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const assetId = useCurrentAssetId();
  // An album is a memory: it opens and is edited in Mémoire de la maison.
  const memoryPath = `/asset/${assetId}/memories/${memory.id}`;
  const [opened, { open, close }] = useDisclosure(false);
  // Le menu « Modifier » ouvre la modale directement en édition, pas en consultation.
  const [shouldEdit, setShouldEdit] = useState(false);

  useEffect(() => {
    if (!shouldOpenModal) return;

    open();
  }, [shouldOpenModal]);

  useEffect(() => {
    if (opened) {
      onModalOpened?.();
    }
  }, [opened]);

  // Album title lives in `content`. Albums created before it became mandatory have
  // none: fall back to the creation date so every tile shows something meaningful.
  const langKey = i18n.language.split('-')[0] as 'en' | 'fr';
  const name =
    (memory.content[langKey] ?? memory.content.en ?? '').trim() ||
    t('community.albumFallback', 'Album du {{date}}', {
      date: dayjs(memory.createdAt).format('DD/MM/YYYY'),
    });

  const { mutate: deleteMemory, isLoading: isDeleting } = useDeleteCommunityFeedItem();

  const openEdit = () => navigate(`${memoryPath}/edit`);

  const confirmDelete = () =>
    modals.openConfirmModal({
      title: t('community.album.deleteTitle', 'Supprimer cet album ?'),
      children: t(
        'community.album.deleteMessage',
        '« {{name}} » et ses {{count}} photos seront retirés d’Entre nous pour tout le monde. Les photos déjà classées dans Documents y restent.',
        { name, count: memory.files.length }
      ),
      labels: {
        confirm: t('community.album.delete', "Supprimer l'album"),
        cancel: t('community.album.keep', 'Garder'),
      },
      confirmProps: { color: 'red' },
      onConfirm: () =>
        deleteMemory(
          { feedItemId: memory.id },
          {
            onSuccess: () => {
              notifications.show({
                message: t('community.album.deleted', 'Album supprimé.'),
                color: 'teal',
              });
            },
          }
        ),
    });

  const photo = memory.files[0];

  if (!photo) return null;

  return (
    <>
      <Modal
        lockScroll={false}
        opened={opened}
        onClose={() => {
          setShouldEdit(false);
          close();
        }}
        withCloseButton={false}
        size="75rem"
        styles={{ body: { padding: 0 } }}
        centered
      >
        <MemoryModal memory={memory} closeModal={close} startInEdit={shouldEdit} />
      </Modal>

      <div className={styles.tile}>
        {/*
        Menu de gestion. `canManage` vient de l'API (auteur, propriétaire ou gestionnaire) :
        le front ne rejoue pas la règle de droits, il masque le menu quand l'action finirait
        en 403. C'est ce qui rend enfin modifiable un album publié par quelqu'un d'autre.
      */}
        {memory.canManage ? (
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <ActionIcon
                className={styles.manageMenu}
                variant="white"
                radius="xl"
                size="lg"
                aria-label={t('community.album.manage', "Gérer l'album")}
                onClick={event => event.stopPropagation()}
              >
                <IconDots size={18} />
              </ActionIcon>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item leftSection={<IconPencil size={16} />} onClick={openEdit}>
                {t('community.album.edit', "Modifier l'album")}
              </Menu.Item>
              <Menu.Item
                color="red"
                leftSection={<IconTrash size={16} />}
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {t('community.album.delete', "Supprimer l'album")}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        ) : null}

        <UnstyledButton className={styles.container} onClick={() => navigate(memoryPath)}>
          <div className={styles.image}>
            <BlurhashImage
              blurhash={photo.blurhash ?? ''}
              imageUrl={photo.previewThumbnailUrl ?? ''}
              width={240}
              height={180}
            />
          </div>

          <div className={styles.caption}>
            <span className={styles.name}>{name}</span>
            <span className={styles.meta}>
              {memory.author.firstName} ·{' '}
              {t('community.albumPhotosCount', {
                count: memory.files.length,
                defaultValue: '{{count}} photo(s)',
              })}
            </span>
          </div>

          <div className={styles.authorAvatar}>
            <UserAvatar size={24} user={memory.author} />
          </div>
        </UnstyledButton>
      </div>
    </>
  );
};

export default Memory;
